import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createColumnHelper, functionalUpdate, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable, } from '@tanstack/react-table';
import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../../button/src';
import { DatePicker } from '../../../date_picker/src';
import { isRtl, LocaleContext } from '../../../i18n/src';
import { Pagination } from '../../../pagination/src';
import { SearchField } from '../../../search_field/src';
import { Select } from '../../../select/src';
import { ActionBar } from './action_bar/action_bar';
import { TableBody } from './table_body';
import { TableHead } from './table_head';
import { useStickyHeader } from './use_sticky_header';
import { VirtualTableBody } from './virtual_table_body';
const getRowSelectionState = (selectedIds) => {
    return selectedIds.reduce((acc, rowId) => (Object.assign(Object.assign({}, acc), { [rowId]: true })), {});
};
// Deprecated: temporary changes to enable inline table editing
const useSkipper = (disableSkip) => {
    const shouldSkipRef = useRef(true);
    const shouldSkip = shouldSkipRef.current;
    // Wrap a function with this to skip a pagination reset temporarily
    const skip = useCallback(() => {
        shouldSkipRef.current = false;
    }, []);
    useEffect(() => {
        shouldSkipRef.current = true;
    });
    if (disableSkip) {
        return [false, () => { }];
    }
    return [shouldSkip, skip];
};
/**
 * The table component displays information that is organized in a way that is easy to scan.
 */
export const Table = ({ actionBarProps, columns, data: initialData, enableSorting = false, getRowId, hasPagination, initialPageSize = 50, initialSelectedIds = [], selectedIds, isLoading = false, isSelectable = false, isSingleSelect = false, highlightedRowIds = [], labelNoResults = 'No results', labelSearchField = 'Search', manualFiltering = false, manualPagination = false, manualPaginationState, manualSorting = false, manualSortingState, onPaginationChange, onRowSelectionChange, onSearch, onSearchClear, onSortingChange, pageCount, paginationNavigationScreenReaderLabel, searchFunction, searchKey, useInternalSearch, filterFields = [], filterByDate, onFilter, onFilterClear, initialSelectedFilters = {}, disableActionBar = false, isEditMode = false, hoistTableObject, customHeaderContent, customHeaderContentPlacement = 'end', enableRowSelection, disableRowSelectionReason, getRowCustomClasses, expandableContentField = 'expandableContent', getExpandableContentField = 'getExpandableContent', getExpandedContentClasses, useVirtualScrolling = false, onLoadMore, hasNextPage, isLoadingMore, loadMoreThreshold, overscanDistance, customScrollParent, autoColumnWidths = false, initialColumnPinning, enableColumnResizing = false, columnSizing, onColumnSizingChange, }) => {
    // Deprecated: temporary changes to enable inline table editing
    const [data, setData] = useState(initialData);
    // Deprecated: temporary changes to enable inline table editing
    const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper(manualPagination || isEditMode);
    const { isStickyShowing, tableContainerRef, actionBarContainerRef, xScrollingRef, stickyTableRef, defaultTableRef, canScrollLeft, canScrollRight, syncStickyHeaderWidths, } = useStickyHeader();
    const locale = useContext(LocaleContext);
    const [selectedFilters, setSelectedFilters] = useState(initialSelectedFilters !== null && initialSelectedFilters !== void 0 ? initialSelectedFilters : {});
    const [internalSearchKey, setInternalSearchKey] = useState('');
    const [rowSelection, setRowSelection] = useState(getRowSelectionState(initialSelectedIds));
    const isRowSelectionInit = useRef(false);
    const [expandedRowIds, setExpandedRowIds] = useState(new Set());
    const [loadingRowIds, setLoadingRowIds] = useState(new Set());
    const [asyncContentMap, setAsyncContentMap] = useState(new Map());
    const previousDataRef = useRef(initialData);
    const rowHasExpandableContent = useCallback((rowData) => {
        if (expandableContentField && rowData && typeof rowData === 'object' && expandableContentField in rowData) {
            const content = rowData[expandableContentField];
            if (content !== undefined && content !== null) {
                return true;
            }
        }
        if (getExpandableContentField &&
            rowData &&
            typeof rowData === 'object' &&
            getExpandableContentField in rowData &&
            typeof rowData[getExpandableContentField] === 'function') {
            return true;
        }
        return false;
    }, [expandableContentField, getExpandableContentField]);
    const hasExpandableRows = useMemo(() => {
        return initialData.some((row) => rowHasExpandableContent(row));
    }, [initialData, rowHasExpandableContent]);
    const handleToggleExpand = useCallback((rowId) => {
        setExpandedRowIds((prev) => {
            const next = new Set(prev);
            if (next.has(rowId)) {
                next.delete(rowId);
            }
            else {
                next.add(rowId);
            }
            return next;
        });
    }, []);
    /**
     * This function is passed to the globalFilterFn of the table as the function to filter rows when internal
     * searching is enabled. By default, we just cast all values into a string (so numbers are included) and compare
     * that to a lower cased version of the search key. This can be overridden with the `searchFunction` prop.
     * More information on filter functions can be found here: https://tanstack.com/table/v8/docs/api/features/filters#filterfn
     */
    const simpleFilterFunction = useCallback((row, columnId, value) => {
        return String(row.getValue(columnId)).toLowerCase().indexOf(String(value).toLowerCase()) !== -1;
    }, []);
    /**
     * If the getRowId prop has been passed in, we use it to determine the unique id for each row. If the passed in key results in an undefined id, we fall back to the table default.
     * The table defaults to using the index of the row from its source data array for its id. In the case of server-side pagination, that index will always be
     * between 0 and the page size, since server is only returning the requested page. So we modify the default logic to account for manualPaginationState.
     */
    const internalGetRowId = useCallback((originalRow, index, parent) => {
        let rowId;
        if (typeof getRowId === 'function') {
            return getRowId(originalRow, index, parent);
        }
        else if (typeof getRowId === 'string') {
            const id = originalRow[getRowId];
            if (id !== undefined && id !== null && (typeof id === 'string' || typeof id === 'number')) {
                rowId = id;
            }
        }
        if (!rowId) {
            rowId = manualPaginationState
                ? manualPaginationState.pageIndex * manualPaginationState.pageSize + index
                : index;
        }
        return `${parent ? [parent.id, rowId].join('.') : rowId}`;
    }, [manualPaginationState, getRowId]);
    /**
     * When the data prop changes, drop cached/loading expandable content for rows that no longer exist (e.g. a new
     * search/filter result set). In virtualized/infinite mode the data array grows as pages are appended, so we prune
     * rather than wipe — keeping already-loaded expanded content for rows that are still present instead of
     * re-fetching it on every page load. The classic path keeps its original wipe-on-change behavior.
     */
    useEffect(() => {
        if (previousDataRef.current === initialData) {
            return;
        }
        previousDataRef.current = initialData;
        if (!useVirtualScrolling) {
            setAsyncContentMap(new Map());
            setLoadingRowIds(new Set());
            return;
        }
        const presentRowIds = new Set(initialData.map((row, index) => internalGetRowId(row, index)));
        setAsyncContentMap((prev) => {
            const next = new Map([...prev].filter(([rowId]) => presentRowIds.has(String(rowId))));
            return next.size === prev.size ? prev : next;
        });
        setLoadingRowIds((prev) => {
            const next = new Set([...prev].filter((rowId) => presentRowIds.has(String(rowId))));
            return next.size === prev.size ? prev : next;
        });
    }, [initialData, useVirtualScrolling, internalGetRowId]);
    const handleFilterChange = (filter) => {
        const newSelectedFilters = Object.assign(Object.assign({}, selectedFilters), { [filter.fieldKey]: Object.assign({}, filter.data) });
        setSelectedFilters(newSelectedFilters);
        onFilter === null || onFilter === void 0 ? void 0 : onFilter(newSelectedFilters);
    };
    const hasNonDefaultFilters = useMemo(() => {
        return Object.entries(selectedFilters).some(([key, filter]) => {
            const field = filterFields.find((f) => f.fieldKey === key);
            if (!(field === null || field === void 0 ? void 0 : field.default)) {
                return true;
            }
            if ('value' in filter && 'value' in field.default) {
                return filter.value !== field.default.value;
            }
            if ('year' in filter &&
                'month' in filter &&
                'day' in filter &&
                'year' in field.default &&
                'month' in field.default &&
                'day' in field.default) {
                return (filter.year !== field.default.year || filter.month !== field.default.month || filter.day !== field.default.day);
            }
            return false;
        });
    }, [selectedFilters, filterFields]);
    const handleClearFilters = () => {
        const resetFilters = {};
        filterFields.forEach((field) => {
            if (field.default) {
                resetFilters[field.fieldKey] = field.default;
            }
        });
        setSelectedFilters(resetFilters);
        onFilterClear === null || onFilterClear === void 0 ? void 0 : onFilterClear();
    };
    useEffect(() => {
        setData(initialData);
    }, [initialData]);
    useEffect(() => {
        if (process.env['NODE_ENV'] !== 'production' && useVirtualScrolling && hasPagination) {
            console.warn('[nucleus Table] `useVirtualScrolling` and `hasPagination` are mutually exclusive; pagination is ignored while virtualization is enabled.');
        }
    }, [useVirtualScrolling, hasPagination]);
    const table = useReactTable(Object.assign(Object.assign(Object.assign(Object.assign({ initialState: Object.assign({}, (initialColumnPinning && { columnPinning: initialColumnPinning })), state: Object.assign(Object.assign(Object.assign({ globalFilter: (internalSearchKey === null || internalSearchKey === void 0 ? void 0 : internalSearchKey.trim()) || searchKey, rowSelection }, (columnSizing && { columnSizing })), (manualPagination && { pagination: manualPaginationState })), (manualSorting && { sorting: manualSortingState })), columns,
        data,
        enableColumnResizing, columnResizeDirection: isRtl(locale) ? 'rtl' : 'ltr', onColumnSizingChange: (updater) => {
            // Supplying this handler replaces TanStack's own state update, so uncontrolled widths are applied here.
            if (columnSizing === undefined) {
                table.setState((old) => (Object.assign(Object.assign({}, old), { columnSizing: functionalUpdate(updater, old.columnSizing) })));
            }
            onColumnSizingChange === null || onColumnSizingChange === void 0 ? void 0 : onColumnSizingChange(updater);
        }, enableGlobalFilter: true, enableRowSelection: enableRowSelection !== null && enableRowSelection !== void 0 ? enableRowSelection : true, enableSorting, getCoreRowModel: getCoreRowModel(), getFilteredRowModel: getFilteredRowModel() }, (hasPagination ? { getPaginationRowModel: getPaginationRowModel() } : {})), { getRowId: internalGetRowId, getSortedRowModel: getSortedRowModel(), globalFilterFn: searchFunction !== null && searchFunction !== void 0 ? searchFunction : simpleFilterFunction, manualFiltering,
        manualPagination,
        manualSorting, enableMultiRowSelection: !isSingleSelect, onRowSelectionChange: setRowSelection, autoResetPageIndex, 
        // Deprecated: temporary changes to enable inline table editing
        meta: {
            data,
            updateData: setData,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            updateColumnData: (rowIndex, columnId, value) => {
                skipAutoResetPageIndex();
                setData((old) => old.map((row, index) => {
                    if (index === rowIndex) {
                        return Object.assign(Object.assign({}, old[rowIndex]), { [columnId]: value });
                    }
                    return row;
                }));
            },
            isEditMode,
        }, pageCount }), (manualPagination && { onPaginationChange })), (manualSorting && { onSortingChange })));
    useEffect(() => {
        if (hasPagination) {
            table.setPageSize(initialPageSize);
        }
        if (hoistTableObject) {
            hoistTableObject(table);
        }
    }, []);
    /**
     * Forwards the selected rows to the consumer but prevents to call it on the react-table initialization
     */
    useEffect(() => {
        if (isRowSelectionInit.current && onRowSelectionChange) {
            onRowSelectionChange(Object.keys(rowSelection));
        }
        isRowSelectionInit.current = true;
    }, [rowSelection]);
    useEffect(() => {
        if (isSelectable && selectedIds) {
            /**
             * isRowSelectionInit.current is set to "false" to prevent the onRowSelectionChange method from being called,
             * which would cause an infinite loop
             */
            isRowSelectionInit.current = false;
            const newSelectedIds = getRowSelectionState(selectedIds);
            setRowSelection(newSelectedIds);
        }
    }, [selectedIds]);
    useEffect(() => {
        if (!hasExpandableRows || !getExpandableContentField || expandedRowIds.size === 0) {
            return;
        }
        // In virtualized mode the body renders the full (un-paginated) row model, so scan that to find expanded rows
        // beyond the first page; the classic path stays on the paginated model.
        const { rows } = useVirtualScrolling ? table.getSortedRowModel() : table.getRowModel();
        const rowsToLoad = [];
        expandedRowIds.forEach((rowId) => {
            if (loadingRowIds.has(rowId) || asyncContentMap.has(rowId)) {
                return;
            }
            const row = rows.find((r) => r.id === rowId);
            if (!row) {
                return;
            }
            const rowData = row.original;
            if (!rowData || typeof rowData !== 'object') {
                return;
            }
            if (expandableContentField && expandableContentField in rowData) {
                const content = rowData[expandableContentField];
                if (content !== undefined && content !== null) {
                    return;
                }
            }
            if (getExpandableContentField in rowData) {
                const getter = rowData[getExpandableContentField];
                if (typeof getter === 'function') {
                    rowsToLoad.push(rowId);
                }
            }
        });
        if (rowsToLoad.length > 0) {
            rowsToLoad.forEach((rowId) => {
                const row = rows.find((r) => r.id === rowId);
                if (!row) {
                    return;
                }
                const rowData = row.original;
                const getter = rowData[getExpandableContentField];
                setLoadingRowIds((prev) => new Set(prev).add(rowId));
                getter()
                    .then((resolvedContent) => {
                    setAsyncContentMap((prev) => new Map(prev).set(rowId, resolvedContent));
                })
                    .catch((error) => {
                    console.error('Error loading expanded content:', error);
                    setAsyncContentMap((prev) => new Map(prev).set(rowId, _jsx("div", { className: 'nucleus-text-text-secondary', children: "Error loading content" })));
                })
                    .finally(() => {
                    setLoadingRowIds((prev) => {
                        const next = new Set(prev);
                        next.delete(rowId);
                        return next;
                    });
                });
            });
        }
    }, [hasExpandableRows, expandedRowIds, getExpandableContentField, expandableContentField, table]);
    const { columnSizing: appliedColumnSizing } = table.getState();
    useLayoutEffect(() => {
        if (isStickyShowing) {
            syncStickyHeaderWidths();
        }
    }, [appliedColumnSizing, isStickyShowing, syncStickyHeaderWidths]);
    const selectedRowsLength = Object.keys(table.getState().rowSelection).length;
    const showActionBar = !isSingleSelect && !disableActionBar && isSelectable && selectedRowsLength > 0;
    const { pageIndex } = table.getState().pagination;
    const hasFilters = filterFields.length > 0 || filterByDate;
    const colSpan = table.getFlatHeaders().length +
        (isSelectable ? 1 : 0) +
        (hasExpandableRows ? 1 : 0) +
        (enableColumnResizing ? 1 : 0);
    return (_jsxs("div", { "data-testid": 'nucleus-table--container', className: `nucleus-box-border nucleus-flex nucleus-flex-col nucleus-gap-6 nucleus-font-sans ${useVirtualScrolling ? 'nucleus-h-full nucleus-min-h-0' : ''}`, children: [(useInternalSearch || hasFilters || !!customHeaderContent) && (_jsxs("div", { className: `nucleus-flex nucleus-items-end nucleus-gap-3${customHeaderContentPlacement === 'start' ? ' nucleus-flex-wrap' : ''}`, children: [customHeaderContentPlacement === 'start' && customHeaderContent, useInternalSearch && (_jsx(SearchField, { ariaLabel: labelSearchField, autoComplete: 'off', id: 'search', dataTestId: 'nucleus-table--search-field', value: internalSearchKey, onChange: (event) => {
                            setInternalSearchKey(event.target.value);
                            onSearch === null || onSearch === void 0 ? void 0 : onSearch(event.target.value);
                        }, onClear: () => {
                            setInternalSearchKey('');
                            onSearchClear === null || onSearchClear === void 0 ? void 0 : onSearchClear();
                        }, onSearch: (value) => {
                            setInternalSearchKey(value);
                            onSearch === null || onSearch === void 0 ? void 0 : onSearch(value);
                        }, placeholder: labelSearchField, width: hasFilters || customHeaderContentPlacement === 'start' ? 'base' : 'lg' })), hasFilters && (_jsxs("div", { className: 'nucleus-flex nucleus-flex-wrap nucleus-items-end nucleus-gap-6 w-full', children: [filterFields.map((filterField) => {
                                const filter = selectedFilters[filterField.fieldKey];
                                const hasCustomWidth = !!filterField.fieldWidth && filterField.fieldWidth !== 'full';
                                return (_jsx("div", { className: hasCustomWidth ? 'flex-none' : 'flex-1', children: _jsx(Select, { options: filterField.filterOptions, onChange: (option) => handleFilterChange({
                                            data: option,
                                            fieldKey: filterField.fieldKey,
                                            isDate: false,
                                        }), label: filterField.fieldLabel || filterField.fieldName, value: filter && 'label' in filter ? filter : undefined, width: filterField.fieldWidth || 'full' }, filterField.fieldName) }, filterField.fieldName));
                            }), filterByDate && (_jsx(DatePicker, { id: 'date-filter-input', label: 'Filter by date', onChange: (date) => {
                                    if (date) {
                                        handleFilterChange({
                                            data: date,
                                            fieldKey: 'date',
                                            isDate: true,
                                        });
                                    }
                                } })), onFilterClear && hasNonDefaultFilters && (_jsx(Button, { label: 'Clear Filters', variant: 'transparent', onClick: handleClearFilters }))] })), customHeaderContentPlacement === 'end' && customHeaderContent] })), useVirtualScrolling && (_jsxs("div", { className: 'nucleus-flex nucleus-min-h-0 nucleus-flex-1 nucleus-flex-col', children: [showActionBar && (_jsx(ActionBar, Object.assign({}, actionBarProps, { 
                        // Virtualized mode has no "page" — select/unselect operate on the whole (filtered) row set.
                        selectAllFn: () => table.toggleAllRowsSelected(true), unselectAllFn: () => table.toggleAllRowsSelected(false), numRows: selectedRowsLength, roundedBorder: true }))), _jsx("div", { className: 'nucleus-min-h-0 nucleus-flex-1', children: _jsx(VirtualTableBody, { table: table, rows: table.getSortedRowModel().rows, colSpan: colSpan, isSelectable: isSelectable, isSingleSelect: isSingleSelect, enableSorting: enableSorting, labelNoResults: labelNoResults, highlightedRowIds: highlightedRowIds, getRowCustomClasses: getRowCustomClasses, disableRowSelectionReason: disableRowSelectionReason, isLoading: isLoading, hasExpandableRows: hasExpandableRows, expandedRowIds: hasExpandableRows ? expandedRowIds : undefined, loadingRowIds: hasExpandableRows ? loadingRowIds : undefined, asyncContentMap: hasExpandableRows ? asyncContentMap : undefined, onToggleExpand: hasExpandableRows ? handleToggleExpand : undefined, getExpandedContentClasses: getExpandedContentClasses, rowHasExpandableContent: rowHasExpandableContent, expandableContentField: expandableContentField, onLoadMore: onLoadMore, hasNextPage: hasNextPage, isLoadingMore: isLoadingMore, loadMoreThreshold: loadMoreThreshold, overscanDistance: overscanDistance, customScrollParent: customScrollParent, autoColumnWidths: autoColumnWidths && !enableColumnResizing, enableColumnResizing: enableColumnResizing }) })] })), !useVirtualScrolling && (_jsxs("div", { ref: tableContainerRef, className: 'nucleus-relative nucleus-box-border nucleus-font-sans [clip-path:inset(0)]', children: [_jsx("div", { className: 'nucleus-z-10 nucleus-box-border nucleus-bg-background', children: showActionBar && (_jsx(ActionBar, Object.assign({}, actionBarProps, { selectAllFn: () => table.toggleAllPageRowsSelected(true), unselectAllFn: () => table.toggleAllPageRowsSelected(false), numRows: selectedRowsLength, roundedBorder: true, ariaHidden: isStickyShowing }))) }), isStickyShowing && (_jsx("div", { ref: actionBarContainerRef, className: 'nucleus-fixed nucleus-z-10 nucleus-box-border nucleus-bg-background nucleus-top-0', children: showActionBar && (_jsx(ActionBar, Object.assign({}, actionBarProps, { selectAllFn: () => table.toggleAllPageRowsSelected(true), unselectAllFn: () => table.toggleAllPageRowsSelected(false), numRows: selectedRowsLength, roundedBorder: false }))) })), _jsxs("div", { className: 'nucleus-relative', children: [_jsxs("div", { ref: xScrollingRef, className: `nucleus-box-border nucleus-w-full nucleus-overflow-x-auto nucleus-border-solid nucleus-font-sans nucleus-rounded-lg nucleus-border nucleus-border-b-0 nucleus-border-neutral-200 dark:nucleus-border-neutral-300 [clip-path:inset(0)]
          ${showActionBar ? 'nucleus-rounded-t-none' : ''} ${hasPagination && table.getPageCount() > 1 ? 'nucleus-rounded-b-none' : ''}`, children: [isStickyShowing && (_jsx("table", { ref: stickyTableRef, className: `nucleus-fixed nucleus-z-10 nucleus-box-border nucleus-w-auto nucleus-table-auto nucleus-border-separate nucleus-border-spacing-0 nucleus-border-solid nucleus-indent-0 nucleus-font-sans nucleus-tabular-nums nucleus-lining-nums nucleus-top-0 nucleus-border-0 nucleus-border-[inherit] ${showActionBar ? 'nucleus-top-16' : 'nucleus-top-0'}`, children: _jsx(TableHead, { isSelectable: isSelectable, roundedBorder: false, flatHeaders: table.getFlatHeaders(), enableSorting: enableSorting, enableExpandableRows: hasExpandableRows, enableColumnResizing: enableColumnResizing }) })), _jsxs("table", { ref: defaultTableRef, className: `nucleus-box-border nucleus-w-full ${enableColumnResizing ? 'nucleus-table-fixed' : 'nucleus-table-auto'} nucleus-border-separate nucleus-border-spacing-0 nucleus-border-solid nucleus-indent-0 nucleus-font-sans nucleus-tabular-nums nucleus-lining-nums nucleus-border-0 nucleus-border-[inherit]`, children: [_jsx(TableHead, { isSelectable: isSelectable, roundedBorder: showActionBar, flatHeaders: table.getFlatHeaders(), ariaHidden: isStickyShowing, enableSorting: enableSorting, enableExpandableRows: hasExpandableRows, enableColumnResizing: enableColumnResizing }), _jsx(TableBody, { colSpan: colSpan, hasFillerCell: enableColumnResizing, highlightedRowIds: highlightedRowIds, getRowCustomClasses: getRowCustomClasses, disableRowSelectionReason: disableRowSelectionReason, isLoading: isLoading, skeletonRowCount: hasPagination ? initialPageSize : 5, isSelectable: isSelectable, isSingleSelect: isSingleSelect, labelNoResults: labelNoResults, rows: table.getRowModel().rows, showRoundedBottomBorder: !hasPagination || table.getPageCount() <= 1, expandedRowIds: hasExpandableRows ? expandedRowIds : undefined, loadingRowIds: hasExpandableRows ? loadingRowIds : undefined, asyncContentMap: hasExpandableRows ? asyncContentMap : undefined, onToggleExpand: hasExpandableRows ? handleToggleExpand : undefined, getExpandedContentClasses: getExpandedContentClasses, rowHasExpandableContent: rowHasExpandableContent, expandableContentField: expandableContentField })] })] }), canScrollLeft && (_jsx("div", { "aria-hidden": 'true', className: 'nucleus-pointer-events-none nucleus-absolute nucleus-inset-y-0 nucleus-w-4 nucleus-z-20', style: {
                                    left: table.getLeftLeafColumns().reduce((acc, col) => acc + col.getSize(), 0),
                                    background: 'linear-gradient(to right, rgba(0,0,0,0.04), transparent)',
                                } })), canScrollRight && (_jsx("div", { "aria-hidden": 'true', className: 'nucleus-pointer-events-none nucleus-absolute nucleus-inset-y-0 nucleus-w-4 nucleus-z-20', style: {
                                    right: table.getRightLeafColumns().reduce((acc, col) => acc + col.getSize(), 0),
                                    background: 'linear-gradient(to left, rgba(0,0,0,0.04), transparent)',
                                } }))] }), hasPagination && table.getPageCount() > 1 && (_jsx("div", { "data-testid": 'nucleus-table--page-size', className: 'nucleus-box-border nucleus-flex nucleus-w-full nucleus-flex-wrap nucleus-items-center nucleus-justify-end nucleus-gap-4 nucleus-border-solid nucleus-bg-neutral-50 nucleus-font-sans nucleus-rounded-b-lg nucleus-border-1 nucleus-border-t-0 nucleus-border-neutral-200 dark:nucleus-border-neutral-300 nucleus-px-6 nucleus-py-3', children: _jsx(Pagination, { count: table.getPageCount(), page: pageIndex + 1, onChange: (pageNumber) => table.setPageIndex(pageNumber - 1), ariaLabel: paginationNavigationScreenReaderLabel, disabled: isLoading, dataTestId: 'nucleus-table--pagination' }) }))] }))] }));
};
export { createColumnHelper };
//# sourceMappingURL=table.js.map